/**
 * Document Management Page
 * CRUD for documents — includes traditional documents and mimir entities.
 * Shows linked standards/entities via object_relations.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, Badge, Spinner, TextInput, Textarea, Select, Modal, Table, Pagination } from 'flowbite-react';
import { HiPlus, HiPencil, HiArchive, HiSearch, HiRefresh, HiLink, HiDownload, HiChevronDown, HiChevronUp, HiClipboardCopy, HiEye } from 'react-icons/hi';
import { useStatus } from '../contexts/StatusContext';
import { documentsApi } from '../api/documents';
import type { CorpusDocument, DocumentCreateInput, DocumentUpdateInput, ObjectRelation } from '../api/documents';

const DOCUMENT_TYPES = [
  'patent', 'contract', 'brand', 'policy', 'architecture', 'registry',
  'explainer', 'api', 'specification', 'runbook', 'decision', 'reference'
];

const OBJECT_TYPES = [
  { value: '', label: 'All types' },
  { value: 'document', label: 'Documents' },
  { value: 'entity', label: 'Mimir Entities' },
  { value: 'reference', label: 'Mimir References' },
  { value: 'competitive_intel', label: 'Competitive Intel' },
];

const objectTypeBadge = (ot: string | null) => {
  switch (ot) {
    case 'entity': return { color: 'purple' as const, label: 'entity' };
    case 'reference': return { color: 'blue' as const, label: 'reference' };
    case 'competitive_intel': return { color: 'pink' as const, label: 'competitive' };
    default: return { color: 'gray' as const, label: 'document' };
  }
};

const EMPTY_FORM: DocumentCreateInput = {
  name: '', document_type: '', abstract: '', version: '',
  status: 'active', location: '', domain: '', object_type: 'document',
};

const DocumentManagement: React.FC = () => {
  const { user } = useStatus();
  const [documents, setDocuments] = useState<CorpusDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [objectTypeFilter, setObjectTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 50;

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState<CorpusDocument | null>(null);
  const [formData, setFormData] = useState<DocumentCreateInput>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  // Archive confirmation
  const [archiveTarget, setArchiveTarget] = useState<CorpusDocument | null>(null);
  const [archiveReason, setArchiveReason] = useState('');

  // Expandable storage detail
  const [expandedDocId, setExpandedDocId] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = (text: string, fieldId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldId);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const [viewingDocId, setViewingDocId] = useState<string | null>(null);

  const handleViewContent = async (docId: string) => {
    setViewingDocId(docId);
    try {
      const resp = await documentsApi.getContentUrl(docId);
      if (resp.success && resp.data) {
        const data = (resp.data as any).data || resp.data;
        window.open(data.url, '_blank');
      } else {
        setError(resp.error || 'Failed to get document content URL');
      }
    } catch {
      setError('Failed to get document content');
    } finally {
      setViewingDocId(null);
    }
  };

  // Relations panel
  const [relationsDocId, setRelationsDocId] = useState<string | null>(null);
  const [relations, setRelations] = useState<ObjectRelation[]>([]);
  const [loadingRelations, setLoadingRelations] = useState(false);

  const loadDocuments = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await documentsApi.list({
        q: searchTerm || undefined,
        document_type: typeFilter || undefined,
        object_type: objectTypeFilter || undefined,
        status: statusFilter || undefined,
        include_archived: statusFilter === 'archived',
        page: currentPage,
        limit: pageSize,
      });
      if (resp.success && resp.data) {
        const data = (resp.data as any).data || resp.data;
        setDocuments(data.Records || []);
        setTotalCount(data.pagination?.total || 0);
        setTotalPages(data.pagination?.total_pages || 1);
      } else {
        setError(resp.error || 'Failed to load documents');
      }
    } catch (err) {
      setError('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, typeFilter, objectTypeFilter, statusFilter, currentPage]);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  // Add link form
  const [linkTargetId, setLinkTargetId] = useState('');
  const [linkTargetType, setLinkTargetType] = useState('invariant');
  const [linkRelationType, setLinkRelationType] = useState('related_to');
  const [isLinking, setIsLinking] = useState(false);

  const handleAddLink = async () => {
    if (!relationsDocId || !linkTargetId) return;
    setIsLinking(true);
    try {
      const doc = documents.find(d => d.document_id === relationsDocId);
      const resp = await documentsApi.createRelation({
        source_object_id: relationsDocId,
        source_object_type: doc?.object_type || 'document',
        target_object_id: linkTargetId.trim(),
        target_object_type: linkTargetType,
        relation_type: linkRelationType,
      });
      if (resp.success) {
        setLinkTargetId('');
        loadRelations(relationsDocId);
        setSuccessMsg('Relation created');
      } else {
        setError(resp.error || 'Failed to create relation');
      }
    } catch (err) {
      setError('Failed to create relation');
    } finally {
      setIsLinking(false);
    }
  };

  const handleExport = async () => {
    try {
      await documentsApi.downloadExport({
        object_type: objectTypeFilter || undefined,
        include_archived: statusFilter === 'archived',
      });
      setSuccessMsg('Documents exported');
    } catch {
      setError('Export failed');
    }
  };

  const loadRelations = async (docId: string) => {
    setRelationsDocId(docId);
    setLoadingRelations(true);
    try {
      const resp = await documentsApi.getRelations(docId);
      if (resp.success && resp.data) {
        const data = (resp.data as any).data || resp.data;
        setRelations(data.relations || []);
      }
    } catch (err) {
      setRelations([]);
    } finally {
      setLoadingRelations(false);
    }
  };

  const openCreate = () => {
    setEditingDoc(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (d: CorpusDocument) => {
    setEditingDoc(d);
    setFormData({
      name: d.name || '',
      document_type: d.document_type || '',
      abstract: d.abstract || '',
      version: d.version || '',
      status: d.status || 'active',
      location: d.location || '',
      domain: d.domain || '',
      object_type: d.object_type || 'document',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      if (editingDoc) {
        const updates: DocumentUpdateInput = {};
        const keys = Object.keys(formData) as (keyof DocumentCreateInput)[];
        for (const key of keys) {
          const oldVal = (editingDoc as any)[key];
          const newVal = formData[key];
          if (newVal !== oldVal && newVal !== '') {
            (updates as any)[key] = newVal;
          }
        }
        if (Object.keys(updates).length === 0) {
          setShowModal(false);
          return;
        }
        const resp = await documentsApi.update(editingDoc.document_id, updates);
        if (!resp.success) { setError(resp.error || 'Update failed'); return; }
        setSuccessMsg(`Updated: ${editingDoc.name}`);
      } else {
        if (!formData.name || !formData.document_type) {
          setError('name and document_type are required');
          return;
        }
        const resp = await documentsApi.create(formData);
        if (!resp.success) { setError(resp.error || 'Create failed'); return; }
        setSuccessMsg(`Created: ${formData.name}`);
      }
      setShowModal(false);
      loadDocuments();
    } catch (err) {
      setError('Save failed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    try {
      const resp = await documentsApi.archive(archiveTarget.document_id, archiveReason);
      if (!resp.success) { setError(resp.error || 'Archive failed'); return; }
      setSuccessMsg(`Archived: ${archiveTarget.name}`);
      setArchiveTarget(null);
      setArchiveReason('');
      loadDocuments();
    } catch (err) {
      setError('Archive failed');
    }
  };

  const uniqueDocTypes = [...new Set(documents.map(d => d.document_type).filter(Boolean))].sort();

  if (isLoading && documents.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Document Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            {totalCount} document{totalCount !== 1 ? 's' : ''} — includes registered documents and mimir entities
          </p>
        </div>
        <div className="flex gap-2">
          <Button color="light" size="sm" onClick={handleExport}>
            <HiDownload className="mr-1 h-4 w-4" /> Export JSON
          </Button>
          <Button color="light" size="sm" onClick={loadDocuments}>
            <HiRefresh className="mr-1 h-4 w-4" /> Refresh
          </Button>
          <Button size="sm" onClick={openCreate}>
            <HiPlus className="mr-1 h-4 w-4" /> New Document
          </Button>
        </div>
      </div>

      {error && (
        <Card className="bg-red-50 border-red-200">
          <p className="text-red-700 text-sm">{error}</p>
        </Card>
      )}
      {successMsg && (
        <Card className="bg-green-50 border-green-200">
          <p className="text-green-700 text-sm">{successMsg}</p>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px]">
            <TextInput
              icon={HiSearch}
              placeholder="Search name, abstract..."
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              sizing="sm"
            />
          </div>
          <Select sizing="sm" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setCurrentPage(1); }}>
            <option value="">All doc types</option>
            {uniqueDocTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </Select>
          <Select sizing="sm" value={objectTypeFilter} onChange={e => { setObjectTypeFilter(e.target.value); setCurrentPage(1); }}>
            {OBJECT_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
          <Select sizing="sm" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
            <option value="">All active</option>
            <option value="draft">Drafts only</option>
            <option value="archived">Archived</option>
          </Select>
        </div>
      </Card>

      {/* Documents Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table hoverable>
            <Table.Head>
              <Table.HeadCell></Table.HeadCell>
              <Table.HeadCell>Name</Table.HeadCell>
              <Table.HeadCell>Type</Table.HeadCell>
              <Table.HeadCell>Object</Table.HeadCell>
              <Table.HeadCell>Domain</Table.HeadCell>
              <Table.HeadCell>Series</Table.HeadCell>
              <Table.HeadCell>Status</Table.HeadCell>
              <Table.HeadCell>Updated</Table.HeadCell>
              <Table.HeadCell>Actions</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {documents.map(d => {
                const otBadge = objectTypeBadge(d.object_type);
                const isExpanded = expandedDocId === d.document_id;
                const seriesLabel = d.series && d.sequence != null
                  ? `${d.series}-${String(d.sequence).padStart(3, '0')}`
                  : null;
                return (
                  <React.Fragment key={d.document_id}>
                    <Table.Row className={`${d.status === 'archived' ? 'opacity-50' : ''} ${isExpanded ? 'bg-gray-50 dark:bg-gray-800' : ''}`}>
                      <Table.Cell className="w-8 px-2">
                        <button
                          onClick={() => setExpandedDocId(isExpanded ? null : d.document_id)}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          {isExpanded
                            ? <HiChevronUp className="h-4 w-4" />
                            : <HiChevronDown className="h-4 w-4" />}
                        </button>
                      </Table.Cell>
                      <Table.Cell className="max-w-[250px]">
                        <div className="truncate font-medium" title={d.name}>{d.name}</div>
                        {d.abstract && <div className="text-xs text-gray-400 truncate" title={d.abstract}>{d.abstract}</div>}
                      </Table.Cell>
                      <Table.Cell>
                        <Badge color="indigo" size="xs">{d.document_type}</Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge color={otBadge.color} size="xs">{otBadge.label}</Badge>
                        {d.entity_type && <span className="text-xs text-gray-400 ml-1">{d.entity_type}</span>}
                      </Table.Cell>
                      <Table.Cell className="text-xs">{d.domain || '-'}</Table.Cell>
                      <Table.Cell>
                        {seriesLabel
                          ? <Badge color="warning" size="xs">{seriesLabel}</Badge>
                          : <span className="text-xs text-gray-400">-</span>}
                      </Table.Cell>
                      <Table.Cell>
                        <Badge color={d.status === 'active' ? 'success' : 'gray'} size="xs">{d.status}</Badge>
                      </Table.Cell>
                      <Table.Cell className="text-xs text-gray-400">
                        {d.updated_at ? new Date(d.updated_at).toLocaleDateString() : '-'}
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex gap-1">
                          <Button size="xs" color="light" onClick={() => loadRelations(d.document_id)} title="Relations">
                            <HiLink className="h-3 w-3" />
                          </Button>
                          <Button size="xs" color="light" onClick={() => openEdit(d)} title="Edit">
                            <HiPencil className="h-3 w-3" />
                          </Button>
                          {d.status !== 'archived' && (
                            <Button size="xs" color="failure" onClick={() => setArchiveTarget(d)} title="Archive">
                              <HiArchive className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                    {isExpanded && (
                      <Table.Row>
                        <Table.Cell colSpan={9} className="bg-gray-50 dark:bg-gray-800 px-6 py-4">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500 text-xs block">Document ID</span>
                              <div className="flex items-center gap-1">
                                <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono">{d.document_id.slice(0, 8)}...</code>
                                <button onClick={() => copyToClipboard(d.document_id, `id-${d.document_id}`)} className="text-gray-400 hover:text-gray-600" title="Copy full ID">
                                  <HiClipboardCopy className="h-3.5 w-3.5" />
                                </button>
                                {copiedField === `id-${d.document_id}` && <span className="text-xs text-green-600">Copied!</span>}
                              </div>
                            </div>
                            {d.registered_by && (
                              <div>
                                <span className="text-gray-500 text-xs block">Registered By</span>
                                <span className="text-xs">{d.registered_by}</span>
                              </div>
                            )}
                            {d.location && (
                              <div>
                                <span className="text-gray-500 text-xs block">Location</span>
                                <span className="text-xs truncate block" title={d.location}>{d.location}</span>
                              </div>
                            )}
                            {d.source_url && (
                              <div>
                                <span className="text-gray-500 text-xs block">Source URL</span>
                                <span className="text-xs truncate block" title={d.source_url}>{d.source_url}</span>
                              </div>
                            )}
                            {d.s3_key && (
                              <>
                                <div className="col-span-2 md:col-span-3">
                                  <Button
                                    size="xs"
                                    color="blue"
                                    onClick={() => handleViewContent(d.document_id)}
                                    disabled={viewingDocId === d.document_id}
                                  >
                                    <HiEye className="mr-1 h-3.5 w-3.5" />
                                    {viewingDocId === d.document_id ? 'Opening...' : 'View Document'}
                                  </Button>
                                </div>
                                <div>
                                  <span className="text-gray-500 text-xs block">S3 Key</span>
                                  <div className="flex items-center gap-1">
                                    <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono">{d.s3_key}</code>
                                    <button onClick={() => copyToClipboard(d.s3_key!, `s3-${d.document_id}`)} className="text-gray-400 hover:text-gray-600" title="Copy S3 key">
                                      <HiClipboardCopy className="h-3.5 w-3.5" />
                                    </button>
                                    {copiedField === `s3-${d.document_id}` && <span className="text-xs text-green-600">Copied!</span>}
                                  </div>
                                </div>
                                {d.s3_version_id && (
                                  <div>
                                    <span className="text-gray-500 text-xs block">S3 Version</span>
                                    <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono">{d.s3_version_id.slice(0, 12)}...</code>
                                  </div>
                                )}
                                {d.content_sha256 && (
                                  <div>
                                    <span className="text-gray-500 text-xs block">SHA-256</span>
                                    <div className="flex items-center gap-1">
                                      <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono">{d.content_sha256.slice(0, 16)}...</code>
                                      <button onClick={() => copyToClipboard(d.content_sha256!, `hash-${d.document_id}`)} className="text-gray-400 hover:text-gray-600" title="Copy full hash">
                                        <HiClipboardCopy className="h-3.5 w-3.5" />
                                      </button>
                                      {copiedField === `hash-${d.document_id}` && <span className="text-xs text-green-600">Copied!</span>}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                            {d.tags && d.tags.length > 0 && (
                              <div>
                                <span className="text-gray-500 text-xs block">Tags</span>
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {d.tags.map(t => <Badge key={t} color="gray" size="xs">{t}</Badge>)}
                                </div>
                              </div>
                            )}
                            <div>
                              <span className="text-gray-500 text-xs block">Created</span>
                              <span className="text-xs">{d.created_at ? new Date(d.created_at).toLocaleString() : '-'}</span>
                            </div>
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </React.Fragment>
                );
              })}
              {documents.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={9} className="text-center text-gray-500 py-8">
                    No documents found
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
        {totalPages > 1 && (
          <div className="flex justify-center mt-4">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          </div>
        )}
      </Card>

      {/* Relations Panel */}
      {relationsDocId && (
        <Card>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold">
              Relations for: {documents.find(d => d.document_id === relationsDocId)?.name || relationsDocId}
            </h3>
            <Button size="xs" color="light" onClick={() => setRelationsDocId(null)}>Close</Button>
          </div>
          {loadingRelations ? (
            <Spinner size="sm" />
          ) : relations.length === 0 ? (
            <p className="text-sm text-gray-500 mb-3">No relations yet.</p>
          ) : (
            <div className="space-y-2 mb-4">
              {relations.map(r => (
                <div key={r.relation_id} className="flex items-center gap-2 text-sm border rounded p-2">
                  <Badge color="blue" size="xs">{r.relation_type}</Badge>
                  <span className="text-gray-400">{r.direction === 'outgoing' ? '→' : '←'}</span>
                  <Badge color={r.target_type === 'invariant' ? 'warning' : 'gray'} size="xs">{r.target_type}</Badge>
                  <span className="font-medium">{r.target?.name || r.target_id}</span>
                  {r.target?.category && <Badge color="indigo" size="xs">{r.target.category}</Badge>}
                  {r.target?.entity_type && <span className="text-xs text-gray-400">({r.target.entity_type})</span>}
                </div>
              ))}
            </div>
          )}
          <div className="border-t pt-3">
            <p className="text-sm font-medium mb-2">Add Link</p>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <TextInput
                  sizing="sm"
                  placeholder="Target object ID (pattern_id or document_id)"
                  value={linkTargetId}
                  onChange={e => setLinkTargetId(e.target.value)}
                />
              </div>
              <Select sizing="sm" value={linkTargetType} onChange={e => setLinkTargetType(e.target.value)}>
                <option value="invariant">standard/invariant</option>
                <option value="document">document</option>
                <option value="entity">entity</option>
                <option value="reference">reference</option>
                <option value="collection">collection</option>
              </Select>
              <Select sizing="sm" value={linkRelationType} onChange={e => setLinkRelationType(e.target.value)}>
                <option value="related_to">related_to</option>
                <option value="informs">informs</option>
                <option value="extracted_from">extracted_from</option>
                <option value="supersedes">supersedes</option>
                <option value="part_of">part_of</option>
                <option value="recommended_by">recommended_by</option>
                <option value="contradicts">contradicts</option>
              </Select>
              <Button size="sm" onClick={handleAddLink} disabled={isLinking || !linkTargetId}>
                {isLinking ? <Spinner size="sm" /> : 'Link'}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Modal show={showModal} onClose={() => setShowModal(false)} size="3xl">
        <Modal.Header>{editingDoc ? `Edit: ${editingDoc.name}` : 'New Document'}</Modal.Header>
        <Modal.Body>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Name *</label>
              <TextInput
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Patent Filing Strategy"
                sizing="sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Document Type *</label>
              <Select
                sizing="sm"
                value={formData.document_type}
                onChange={e => setFormData(prev => ({ ...prev, document_type: e.target.value }))}
              >
                <option value="">Select...</option>
                {DOCUMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Object Type</label>
              <Select
                sizing="sm"
                value={formData.object_type || 'document'}
                onChange={e => setFormData(prev => ({ ...prev, object_type: e.target.value }))}
              >
                <option value="document">document</option>
                <option value="entity">entity (mimir)</option>
                <option value="reference">reference (mimir)</option>
                <option value="competitive_intel">competitive intel</option>
              </Select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Abstract</label>
              <Textarea
                value={formData.abstract || ''}
                onChange={e => setFormData(prev => ({ ...prev, abstract: e.target.value }))}
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Version</label>
              <TextInput
                value={formData.version || ''}
                onChange={e => setFormData(prev => ({ ...prev, version: e.target.value }))}
                sizing="sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Domain</label>
              <TextInput
                value={formData.domain || ''}
                onChange={e => setFormData(prev => ({ ...prev, domain: e.target.value }))}
                placeholder="e.g. ip-strategy, operations"
                sizing="sm"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Location / URL</label>
              <TextInput
                value={formData.location || ''}
                onChange={e => setFormData(prev => ({ ...prev, location: e.target.value }))}
                placeholder="https://... or file path"
                sizing="sm"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="mr-2" /> : null}
            {editingDoc ? 'Update' : 'Create'}
          </Button>
          <Button color="gray" onClick={() => setShowModal(false)}>Cancel</Button>
        </Modal.Footer>
      </Modal>

      {/* Archive Confirmation */}
      <Modal show={!!archiveTarget} onClose={() => setArchiveTarget(null)} size="md">
        <Modal.Header>Archive Document</Modal.Header>
        <Modal.Body>
          <p className="mb-4">Archive <strong>{archiveTarget?.name}</strong>?</p>
          <TextInput
            placeholder="Reason (optional)"
            value={archiveReason}
            onChange={e => setArchiveReason(e.target.value)}
            sizing="sm"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button color="failure" onClick={handleArchive}>Archive</Button>
          <Button color="gray" onClick={() => { setArchiveTarget(null); setArchiveReason(''); }}>Cancel</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DocumentManagement;
